import React, { useEffect, useRef, useMemo } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { html } from '@codemirror/lang-html';
import { css } from '@codemirror/lang-css';
import { json } from '@codemirror/lang-json';
import { python } from '@codemirror/lang-python';
import { markdown } from '@codemirror/lang-markdown';
import { oneDark } from '@codemirror/theme-one-dark';
import { createTaskingTheme } from '../helpers/codemirrorThemes';
import {
  EditorView,
  keymap,
  lineNumbers,
  highlightActiveLine,
  highlightActiveLineGutter,
} from '@codemirror/view';
import { EditorState, Extension, Compartment } from '@codemirror/state';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import {
  searchKeymap,
  search,
  SearchQuery,
  setSearchQuery,
  findNext,
  findPrevious,
} from '@codemirror/search';
import { bracketMatching, foldGutter, indentOnInput } from '@codemirror/language';
import { autocompletion, completionKeymap } from '@codemirror/autocomplete';

// Type definitions to match Monaco's API
export type CodeMirrorEditorView = EditorView;
export type CodeMirrorPosition = { lineNumber: number; column: number };
export type CodeMirrorSelection = {
  startLineNumber: number;
  startColumn: number;
  endLineNumber: number;
  endColumn: number;
};

interface CodeMirrorEditorProps {
  value?: string;
  onChange?: (value: string | undefined) => void;
  language?: string;
  theme?: string;
  height?: string;
  path?: string;
  defaultLanguage?: string;
  options?: {
    fontSize?: number;
    fontFamily?: string;
    lineNumbers?: 'on' | 'off';
    minimap?: { enabled: boolean };
    scrollBeyondLastLine?: boolean;
    padding?: { top: number; bottom: number };
    wordWrap?: 'on' | 'off';
    readOnly?: boolean;
    tabSize?: number;
    insertSpaces?: boolean;
    // Performance options
    enableAutocomplete?: boolean;
    enableLinting?: boolean;
    [key: string]: any;
  };
  /** Additional extensions (e.g. LSP go-to-definition, hover, linting) */
  extraExtensions?: Extension[];
  onMount?: (editor: EditorView, monaco: any) => void;
  beforeMount?: (monaco: any) => void;
  loading?: React.ReactNode;
}

// Language mapping - maps Monaco language names to CodeMirror extensions
const languageMap: Record<string, () => Extension> = {
  // JavaScript/TypeScript variants
  javascript: () => javascript({ jsx: true, typescript: false }),
  typescript: () => javascript({ jsx: true, typescript: true }),
  javascriptreact: () => javascript({ jsx: true, typescript: false }),
  typescriptreact: () => javascript({ jsx: true, typescript: true }),
  jsx: () => javascript({ jsx: true, typescript: false }),
  tsx: () => javascript({ jsx: true, typescript: true }),
  // Web languages
  html: () => html(),
  css: () => css(),
  scss: () => css(),
  sass: () => css(),
  less: () => css(),
  // Data formats
  json: () => json(),
  // Other languages
  python: () => python(),
  markdown: () => markdown(),
  // Default to empty extension for unsupported languages
  plaintext: () => [],
};

// Create theme extensions based on Monaco theme names
const createThemeExtension = (themeName: string): Extension[] => {
  if (themeName === 'tasking-dark') {
    return createTaskingTheme('tasking-dark');
  }
  if (themeName === 'tasking-light' || themeName === 'vs') {
    return createTaskingTheme('tasking-light');
  }
  // Fallback to oneDark for vs-dark
  if (themeName === 'vs-dark') {
    return [oneDark];
  }
  // Default to light theme
  return createTaskingTheme('tasking-light');
};

/**
 * Create extensions based on options
 *
 * PERFORMANCE OPTIMIZATIONS:
 * - Autocomplete is optional and disabled for large files (>100KB) to reduce CPU usage
 * - Linting is disabled by default as it can be expensive
 * - Only essential features are always enabled (syntax highlighting, line numbers, etc.)
 * - Editor is lazy-loaded (only mounted when tab is visible, no forceMount)
 */
const createExtensions = (
  language: string,
  themeName: string,
  options: CodeMirrorEditorProps['options'],
): Extension[] => {
  const extensions: Extension[] = [];

  // Language support - try exact match, then lowercase, then default to empty
  const langExtension = languageMap[language] || languageMap[language.toLowerCase()] || (() => []);
  const langExt = langExtension();
  if (langExt) {
    if (Array.isArray(langExt)) {
      extensions.push(...langExt);
    } else {
      extensions.push(langExt);
    }
  }

  // Theme - createThemeExtension returns an array of extensions
  const themeExtensions = createThemeExtension(themeName);
  extensions.push(...themeExtensions);

  // Basic editor features (always enabled - lightweight)
  extensions.push(
    lineNumbers(),
    highlightActiveLine(),
    highlightActiveLineGutter(),
    history(),
    bracketMatching(),
    indentOnInput(),
  );

  // Optional expensive features - only enable if explicitly requested
  // These can cause performance issues on large files or slow devices
  // Autocomplete is CPU-intensive, especially on large files
  if (options?.enableAutocomplete !== false) {
    extensions.push(autocompletion());
  }

  // Folding can be expensive on large files, but it's generally lightweight
  extensions.push(foldGutter());

  // Options-based extensions
  if (options?.lineNumbers === 'on') {
    extensions.push(lineNumbers());
  }

  // Font size and family
  const fontSize = options?.fontSize || 13;
  const fontFamily = options?.fontFamily || "'JetBrains Mono', 'Fira Code', 'Consolas', monospace";

  // Ensure fontSize is reasonable (was 8 in Monaco options, but should be 13)
  const finalFontSize = fontSize < 10 ? 13 : fontSize;
  extensions.push(
    EditorView.theme({
      '&': {
        fontSize: `${finalFontSize}px`,
        fontFamily: fontFamily,
      },
      '.cm-content': {
        paddingTop: `${options?.padding?.top || 10}px`,
        paddingBottom: `${options?.padding?.bottom || 10}px`,
        fontSize: `${finalFontSize}px`,
        fontFamily: fontFamily,
      },
      '.cm-scroller': {
        fontFamily: fontFamily,
        overflowX: 'auto',
        overflowY: 'scroll',
        // Enable native touch scrolling on mobile
        WebkitOverflowScrolling: 'touch',
        touchAction: 'pan-x pan-y',
      },
      '.cm-lineNumbers': {
        fontSize: `${finalFontSize}px`,
        fontFamily: fontFamily,
      },
    }),
  );

  // Word wrap
  if (options?.wordWrap === 'on') {
    extensions.push(EditorView.lineWrapping);
  }

  // Tab size
  const tabSize = options?.tabSize || 2;
  const insertSpaces = options?.insertSpaces !== false;
  extensions.push(EditorState.tabSize.of(tabSize));
  if (insertSpaces) {
    extensions.push(
      EditorState.changeFilter.of(() => true),
      keymap.of([
        {
          key: 'Tab',
          run: (view) => {
            const spaces = ' '.repeat(tabSize);
            view.dispatch({
              changes: { from: view.state.selection.main.from, insert: spaces },
            });
            return true;
          },
        },
      ]),
    );
  }

  // Read-only
  if (options?.readOnly) {
    extensions.push(EditorState.readOnly.of(true));
  }

  // Keymaps - only include completion keymap if autocomplete is enabled
  const keymaps = [...defaultKeymap, ...historyKeymap, ...searchKeymap];

  if (options?.enableAutocomplete !== false) {
    keymaps.push(...completionKeymap);
  }

  extensions.push(keymap.of(keymaps));

  // Search extension — required for setSearchQuery / findNext / findPrevious to work
  extensions.push(search({ top: true }));

  return extensions;
};

export const CodeMirrorEditor: React.FC<CodeMirrorEditorProps> = ({
  value = '',
  onChange,
  language = 'plaintext',
  theme = 'vs',
  height = '100%',
  options = {},
  extraExtensions,
  onMount,
  beforeMount,
  loading,
}) => {
  const editorRef = useRef<EditorView | null>(null);
  const editorWrapperRef = useRef<any>(null);
  const hasMountedRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const extensionsRef = useRef<Extension[]>([]);

  // Call beforeMount if provided (for compatibility with Monaco API)
  useEffect(() => {
    if (beforeMount && !hasMountedRef.current) {
      // Create a mock monaco object for compatibility
      const mockMonaco = {
        editor: {
          setTheme: () => {},
        },
        languages: {
          typescript: {
            typescriptDefaults: {
              setEagerModelSync: () => {},
              setCompilerOptions: () => {},
              setDiagnosticsOptions: () => {},
              addExtraLib: () => {},
            },
            javascriptDefaults: {
              setEagerModelSync: () => {},
              setCompilerOptions: () => {},
              setDiagnosticsOptions: () => {},
              addExtraLib: () => {},
            },
          },
        },
      };
      beforeMount(mockMonaco);
    }
  }, [beforeMount]);

  // Determine theme name
  const themeName = theme === 'tasking-light' || theme === 'vs' ? 'vs' : 'tasking-dark';

  // Create Monaco-compatible editor wrapper
  const createEditorWrapper = (view: EditorView) => {
    const state = view.state;

    return {
      // Monaco-compatible methods
      getScrolledVisiblePosition: (pos: { lineNumber: number; column: number }) => {
        try {
          const line = state.doc.line(pos.lineNumber);
          const coords = view.coordsAtPos(line.from + pos.column - 1);
          if (!coords) return null;

          const scrollTop = view.scrollDOM.scrollTop;
          const scrollLeft = view.scrollDOM.scrollLeft;

          return {
            top: coords.top - scrollTop,
            left: coords.left - scrollLeft,
            height: coords.bottom - coords.top,
          };
        } catch {
          return null;
        }
      },
      getScrollTop: () => view.scrollDOM.scrollTop,
      getScrollLeft: () => view.scrollDOM.scrollLeft,
      onDidScrollChange: (callback: () => void) => {
        const listener = () => callback();
        view.scrollDOM.addEventListener('scroll', listener);
        return {
          dispose: () => view.scrollDOM.removeEventListener('scroll', listener),
        };
      },
      onDidChangeCursorPosition: (
        callback: (e: { position: { lineNumber: number; column: number } }) => void,
      ) => {
        let disposed = false;
        const updateListener = EditorView.updateListener.of((update) => {
          if (!disposed && update.selectionSet) {
            const main = update.state.selection.main;
            const line = update.state.doc.lineAt(main.head);
            callback({
              position: {
                lineNumber: line.number,
                column: main.head - line.from + 1,
              },
            });
          }
        });
        view.dispatch({ effects: [] });
        return {
          dispose: () => {
            disposed = true;
          },
        };
      },
      onDidChangeCursorSelection: (
        callback: (e: {
          selection: {
            startLineNumber: number;
            startColumn: number;
            endLineNumber: number;
            endColumn: number;
          };
        }) => void,
      ) => {
        let disposed = false;
        const updateListener = EditorView.updateListener.of((update) => {
          if (!disposed && update.selectionSet) {
            const main = update.state.selection.main;
            const startLine = update.state.doc.lineAt(main.from);
            const endLine = update.state.doc.lineAt(main.to);
            callback({
              selection: {
                startLineNumber: startLine.number,
                startColumn: main.from - startLine.from + 1,
                endLineNumber: endLine.number,
                endColumn: main.to - endLine.from + 1,
              },
            });
          }
        });
        return {
          dispose: () => {
            disposed = true;
          },
        };
      },
      getAction: (actionId: string) => {
        return {
          run: () => {
            console.log(`Action ${actionId} called (not implemented in CodeMirror)`);
          },
        };
      },
      addCommand: () => {}, // Commands handled via keymap
      // Search methods
      searchInFile: (query: string) => {
        const q = new SearchQuery({ search: query.trim(), caseSensitive: false });
        view.dispatch({ effects: setSearchQuery.of(q) });
        if (query.trim()) findNext(view);
      },
      findNextInFile: () => {
        findNext(view);
      },
      findPrevInFile: () => {
        findPrevious(view);
      },
    };
  };

  // Map Monaco language names to CodeMirror language names
  const mapLanguage = (lang: string): string => {
    // Map Monaco-specific language names to CodeMirror equivalents
    const langMap: Record<string, string> = {
      typescriptreact: 'tsx',
      javascriptreact: 'jsx',
    };
    return langMap[lang] || lang;
  };

  const mappedLanguage = mapLanguage(language);

  // Create extensions
  const extensions = useMemo(() => {
    const base = createExtensions(mappedLanguage, themeName, options);
    if (extraExtensions?.length) {
      return [...base, ...extraExtensions];
    }
    return base;
  }, [mappedLanguage, themeName, options, extraExtensions]);

  // Handle editor creation
  const handleCreateEditor = (view: EditorView) => {
    editorRef.current = view;
    editorWrapperRef.current = createEditorWrapper(view);

    if (onMount && !hasMountedRef.current) {
      hasMountedRef.current = true;
      // Create a mock monaco object for compatibility
      const mockMonaco = {
        editor: {
          setTheme: () => {},
        },
        KeyMod: {
          CtrlCmd: 2048,
          Shift: 1024,
          Alt: 512,
        },
        KeyCode: {
          KeyS: 'KeyS',
          KeyF: 'KeyF',
          KeyG: 'KeyG',
          KeyD: 'KeyD',
          KeyL: 'KeyL',
          KeyK: 'KeyK',
          Slash: 'Slash',
          BracketRight: 'BracketRight',
          BracketLeft: 'BracketLeft',
          UpArrow: 'UpArrow',
          DownArrow: 'DownArrow',
        },
      };
      onMount(editorWrapperRef.current, mockMonaco);
    }
  };

  // Handle value changes
  const handleChange = (value: string) => {
    if (onChange) {
      onChange(value);
    }
  };

  // Listen for theme changes to ensure editor updates when CSS variables change
  // CSS variables are live and will automatically update the editor styles
  // This listener ensures we're aware of theme changes for any future enhancements
  useEffect(() => {
    const handleThemeChange = (event: CustomEvent) => {
      // Theme change detected - CSS variables are already updated on :root
      // CodeMirror themes use CSS variables, so styles update automatically
      // No action needed, but we log for debugging if needed
      if (process.env.NODE_ENV === 'development') {
        console.log('[CodeMirrorEditor] Theme change detected, CSS variables updated');
      }
    };

    window.addEventListener('tasking-custom-theme', handleThemeChange as EventListener);

    return () => {
      window.removeEventListener('tasking-custom-theme', handleThemeChange as EventListener);
    };
  }, []);

  // Access EditorView after component mounts
  // @uiw/react-codemirror doesn't expose EditorView directly, so we use a workaround
  useEffect(() => {
    if (!containerRef.current || editorRef.current) return;

    // Find the EditorView in the DOM
    const findEditorView = () => {
      const editorElement = containerRef.current?.querySelector('.cm-editor');
      if (editorElement) {
        // Try to find the view - @uiw/react-codemirror may store it differently
        // Check various possible locations
        const view =
          (editorElement as any).__cm_view ||
          (editorElement.parentElement as any)?.__cm_view ||
          (editorElement.querySelector('.cm-scroller') as any)?.__cm_view;

        if (view && view instanceof EditorView && !editorRef.current) {
          handleCreateEditor(view);
        }
      }
    };

    // Try multiple times as the editor may not be ready immediately
    findEditorView();
    const timeouts = [
      setTimeout(findEditorView, 50),
      setTimeout(findEditorView, 100),
      setTimeout(findEditorView, 200),
    ];

    return () => timeouts.forEach(clearTimeout);
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
      }}
      role="textbox"
      aria-label={`Code editor for ${language} file`}
      aria-readonly={options?.readOnly || false}
      tabIndex={-1}
    >
      <CodeMirror
        value={value}
        onChange={handleChange}
        height="100%"
        extensions={extensions}
        theme={undefined}
        basicSetup={false}
      />
    </div>
  );
};
